import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';
import { BsJournalBookmark, BsSearch } from 'react-icons/bs';
import { FaDatabase, FaFolder } from 'react-icons/fa';
import PageHeader from 'Utils/PageHeader';
import AppContext from 'Utils/AppContext';
import { getDisplayDate, getDisplayTime } from 'Utils';
import { getDataSourceNumericId, isFileDatasourceType } from 'DataSources/datasourcesApi';
import { latestInventoryOf, listDataSourceInventories } from 'Secure/api';
import { latestCompletedDiscoverRun } from 'Secure/secureDiscoverFields';
import { loadMergedInventory } from '../MaskingSources/inventoryMerge';
import {
  filterMaskableFiles,
  maskEligibleConnections,
  sourceSubtitle,
  sourceSubtitleTitle,
} from './managerUtils';
import './InventoryManager.css';

/** Sortable column header: click toggles/switches the sort; the active column shows ▲/▼. */
function SortHeader({ label, sortKey, sort, onSort, className }) {
  const active = sort.key === sortKey;
  return (
    <th className={className}>
      <button
        type='button'
        className={`im-sort${active ? ' active' : ''}`}
        onClick={() => onSort(sortKey)}
        data-testid={`im-sort-${sortKey}`}
      >
        {label}
        {active && <span className='im-sort-arrow'>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

/** "Never" for missing timestamps; otherwise "August 2, 2026 · 10:11 PM". */
function DateCell({ value }) {
  if (value == null) return <span className='im-date-never'>Never</span>;
  return (
    <span className='im-date'>
      {getDisplayDate(value)} <span className='im-date-time'>· {getDisplayTime(value)}</span>
    </span>
  );
}

/**
 * Inventory Manager landing page: one row per mask-eligible data source that has discovery
 * results or a saved inventory, with its inventory count, last inventory save, and last
 * completed discover run. The INVENTORY button opens the source's dedicated inventory page
 * (/inventory-manager/:id).
 */
function InventoryManager() {
  const { connections } = useContext(AppContext);
  const history = useHistory();
  const [sourceQuery, setSourceQuery] = useState('');
  // Only sources that actually have discovery results or a saved inventory belong in the list —
  // a source with neither has nothing to edit. `eligibleIds` is null until the probe completes.
  const [eligibleIds, setEligibleIds] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  // Per-source list metadata (connection id → { count, lastSavedAt, lastDiscoverAt,
  // inventories }). lastSavedAt is the newest inventory's updatedAt (epoch ms; null = never
  // saved); lastDiscoverAt is the latest COMPLETED discover run's finish time (epoch ms);
  // inventories is the source's named inventories ([{ id, name, savedAt }]) rendered as
  // sub-rows under the source.
  const [rowMeta, setRowMeta] = useState({});

  const candidates = useMemo(() => maskEligibleConnections(connections), [connections]);
  // Stable identity of the candidate set so the probe doesn't re-run on every AppContext poll
  // (connections is a fresh array each poll even when its contents are unchanged).
  const candidateKey = useMemo(
    () => candidates.map((c) => c.id).sort((a, b) => (a > b ? 1 : -1)).join(','),
    [candidates]
  );

  // Probe each candidate: keep only those with discovery results OR a saved inventory, and
  // collect the row metadata (inventory count + both dates) along the way.
  //
  // Two phases so the list isn't gated on overview downloads:
  //  A (gates the list) — cheap per source: inventory list + run history. No overviews.
  //  B (background)     — file sources only: load the merged inventory and drop sources
  //    with no maskable file (by content-sniffed format — documents, unsniffable content,
  //    unsupported extensions). Such a row disappears shortly after render instead of
  //    delaying the whole page.
  useEffect(() => {
    let cancelled = false;
    const refineFileSources = async (kept) => {
      const fileKept = kept.filter((k) => isFileDatasourceType(k.conn.datasourceType));
      if (fileKept.length === 0) return;
      const toDrop = (
        await Promise.all(
          fileKept.map(async (k) => {
            try {
              const { selectedFields, noDiscoverFields, hasSavedInventory, fileTableMeta } =
                await loadMergedInventory(k.dsId, {}, undefined, k.latest || null);
              if (Object.keys(filterMaskableFiles(selectedFields, k.conn, fileTableMeta)).length === 0) {
                return k.connId;
              }
              if (noDiscoverFields && !hasSavedInventory) return k.connId;
              return null;
            } catch (e) {
              return k.connId; // unreadable → treat as ineligible rather than show a broken row
            }
          })
        )
      ).filter((x) => x != null);
      if (cancelled || toDrop.length === 0) return;
      setEligibleIds((prev) => {
        const next = new Set(prev);
        toDrop.forEach((connId) => next.delete(connId));
        return next;
      });
    };
    const probe = async () => {
      setListLoading(true);
      const results = await Promise.all(
        candidates.map(async (c) => {
          const id = getDataSourceNumericId(c);
          if (id == null) return null;
          try {
            const [invList, latestRun] = await Promise.all([
              listDataSourceInventories(id).catch(() => []),
              latestCompletedDiscoverRun(id).catch(() => null),
            ]);
            if (invList.length === 0 && !latestRun) return null;
            const latest = latestInventoryOf(invList);
            const lastSavedAt =
              latest && latest.updatedAt && !Number.isNaN(Date.parse(latest.updatedAt))
                ? Date.parse(latest.updatedAt)
                : null;
            const lastDiscoverAt = latestRun
              ? latestRun.completed_at || latestRun.start_date || null
              : null;
            // Light per-inventory rows for the sub-list: name + when it was last saved.
            const inventoryRows = invList
              .filter((inv) => inv && inv.id != null)
              .map((inv) => ({
                id: inv.id,
                name: inv.name || `Inventory ${inv.id}`,
                savedAt:
                  inv.updatedAt && !Number.isNaN(Date.parse(inv.updatedAt))
                    ? Date.parse(inv.updatedAt)
                    : null,
              }))
              .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
            return {
              connId: c.id,
              count: invList.length,
              dsId: id,
              conn: c,
              latest,
              lastSavedAt,
              lastDiscoverAt,
              inventories: inventoryRows,
            };
          } catch (e) {
            return null; // unreadable → treat as ineligible rather than show a broken row
          }
        })
      );
      if (cancelled) return;
      const kept = results.filter((x) => x != null);
      setEligibleIds(new Set(kept.map((x) => x.connId)));
      setRowMeta(
        Object.fromEntries(
          kept.map((x) => [
            x.connId,
            {
              count: x.count,
              lastSavedAt: x.lastSavedAt,
              lastDiscoverAt: x.lastDiscoverAt,
              inventories: x.inventories,
            },
          ])
        )
      );
      setListLoading(false);
      refineFileSources(kept);
    };
    if (candidates.length === 0) {
      setEligibleIds(new Set());
      setRowMeta({});
      setListLoading(false);
    } else {
      probe();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey]);

  const sources = useMemo(
    () => (eligibleIds ? candidates.filter((c) => eligibleIds.has(c.id)) : []),
    [candidates, eligibleIds]
  );

  // Column sort. Default: most recent discover first. Clicking a header toggles direction
  // (or switches column, keeping that column's natural direction: text asc, dates/counts desc).
  const [sort, setSort] = useState({ key: 'lastDiscoverAt', dir: 'desc' });
  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );

  const filtered = useMemo(() => {
    const q = sourceQuery.trim().toLowerCase();
    const matched = !q
      ? sources
      : sources.filter(
          (s) =>
            String(s.name || '').toLowerCase().includes(q) ||
            String(sourceSubtitle(s) || '').toLowerCase().includes(q) ||
            String(s.rootPath || '').toLowerCase().includes(q)
        );
    const valueOf = (s) => {
      const meta = rowMeta[s.id] || {};
      switch (sort.key) {
        case 'name':
          return String(s.name || '').toLowerCase();
        case 'count':
          return meta.count || 0;
        case 'lastSavedAt':
          return meta.lastSavedAt;
        case 'lastDiscoverAt':
        default:
          return meta.lastDiscoverAt;
      }
    };
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...matched].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      // "Never" (null) sorts after real values in BOTH directions.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [sources, sourceQuery, rowMeta, sort]);

  // Open the source's dedicated inventory page; a specific inventoryId (sub-row click)
  // rides on navigation state so the page opens with that named inventory selected.
  const openInventory = (s, inventoryId) =>
    history.push(
      `/inventory-manager/${encodeURIComponent(s.id)}`,
      inventoryId != null ? { inventoryId } : undefined
    );

  return (
    <div className='im-page-root'>
      <PageHeader
        header='Inventory Manager'
        icon={BsJournalBookmark}
        tooltip='Review and edit masking inventories across all discovered data sources.'
      />
      <Card>
        <div className='im-shell im-list-shell'>
          <div className='im-list-toolbar'>
            <div className='im-sidebar-title'>
              Data sources <span className='im-scount'>· {filtered.length}</span>
            </div>
            <div className='im-search-wrap'>
              <BsSearch className='im-search-icon' aria-hidden />
              <input
                data-testid='search-inventory-sources'
                className='im-search'
                placeholder='Search sources…'
                value={sourceQuery}
                onChange={(e) => setSourceQuery(e.target.value)}
              />
            </div>
          </div>
          <div className='im-table-wrap'>
            {listLoading ? (
              <div className='im-list-empty'>Loading data sources…</div>
            ) : filtered.length === 0 ? (
              <div className='im-list-empty'>
                {sourceQuery.trim()
                  ? 'No sources match.'
                  : 'No data sources with discovery results or a saved inventory.'}
              </div>
            ) : (
              <table className='im-table' data-testid='im-source-table'>
                <thead>
                  <tr>
                    <SortHeader label='Data source / inventory' sortKey='name' sort={sort} onSort={toggleSort} />
                    <SortHeader label='Inventories' sortKey='count' sort={sort} onSort={toggleSort} className='im-th-num' />
                    <SortHeader label='Last saved' sortKey='lastSavedAt' sort={sort} onSort={toggleSort} />
                    <SortHeader label='Last discover' sortKey='lastDiscoverAt' sort={sort} onSort={toggleSort} />
                    <th aria-label='actions' />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const meta = rowMeta[s.id] || {};
                    const invRows = meta.inventories || [];
                    return (
                      <React.Fragment key={s.id}>
                        <tr
                          className='im-row'
                          onClick={() => openInventory(s)}
                          data-testid={`im-source-row-${s.id}`}
                        >
                          <td>
                            <span className='im-src-cell'>
                              {isFileDatasourceType(s.datasourceType) ? (
                                <FaFolder className='im-src-icon' />
                              ) : (
                                <FaDatabase className='im-src-icon' />
                              )}
                              <span className='im-src-text'>
                                <span className='im-src-name'>{s.name}</span>
                                {/* Hover reveals the full path for file sources (the visible
                                    subtitle is scheme + last two path elements). */}
                                <span className='im-src-meta' title={sourceSubtitleTitle(s)}>
                                  {sourceSubtitle(s)}
                                </span>
                              </span>
                            </span>
                          </td>
                          <td className='im-th-num'>{meta.count || 0}</td>
                          <td data-testid={`im-last-save-${s.id}`}>
                            {/* Sub-rows carry each named inventory's save time; the source row
                                only says "Never" when there is nothing underneath. */}
                            {invRows.length === 0 ? <DateCell value={null} /> : null}
                          </td>
                          <td data-testid={`im-last-discover-${s.id}`}>
                            <DateCell value={meta.lastDiscoverAt} />
                          </td>
                          <td className='im-td-action'>
                            <Button
                              size='sm'
                              variant='outline-primary'
                              onClick={(e) => {
                                e.stopPropagation();
                                openInventory(s);
                              }}
                              data-testid={`im-open-inventory-${s.id}`}
                            >
                              Inventory
                            </Button>
                          </td>
                        </tr>
                        {/* Named inventories of this source, newest first — clicking one opens
                            the inventory page with THAT inventory selected. */}
                        {invRows.map((inv) => (
                          <tr
                            key={`${s.id}-inv-${inv.id}`}
                            className='im-row im-inv-row'
                            onClick={() => openInventory(s, inv.id)}
                            data-testid={`im-inventory-row-${s.id}-${inv.id}`}
                          >
                            <td>
                              <span className='im-inv-cell'>
                                <BsJournalBookmark className='im-inv-row-icon' aria-hidden />
                                <span className='im-inv-row-name'>{inv.name}</span>
                              </span>
                            </td>
                            <td />
                            <td data-testid={`im-inv-saved-${inv.id}`}>
                              <DateCell value={inv.savedAt} />
                            </td>
                            <td />
                            <td />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default InventoryManager;
